import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tmsApi,
  type CreateTripCheckpointTemplateRequest,
  type TripCheckpointPreview,
  type TripCheckpointPreviewRequest,
  type TripCheckpointTemplate,
  type UpdateTripCheckpointTemplateRequest,
} from '../client';

/** Query keys, `'tms'`-prefixed like the other TMS caches. */
export const tripCheckpointTemplateKeys = {
  all: ['tms', 'trip-checkpoint-template'] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'trip-checkpoint-template', 'org', organisationId] as const,
  preview: (request: TripCheckpointPreviewRequest) =>
    ['tms', 'trip-checkpoint-template', 'preview', request] as const,
};

/** Every trip checkpoint template for an organisation. `null` keeps it disabled. */
export function useTripCheckpointTemplates(organisationId: string | null) {
  return useQuery<TripCheckpointTemplate[], Error>({
    queryKey: organisationId
      ? tripCheckpointTemplateKeys.byOrganisation(organisationId)
      : tripCheckpointTemplateKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getTripCheckpointTemplates(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * What one order would get from the templates, the draft being edited
 * included. A POST that only reads, cached by its whole body — the caller
 * debounces the draft so typing does not fire a request per key. The previous
 * answer stays on screen while the next one loads.
 */
export function useTripCheckpointPreview(request: TripCheckpointPreviewRequest | null) {
  return useQuery<TripCheckpointPreview, Error>({
    queryKey: request ? tripCheckpointTemplateKeys.preview(request) : tripCheckpointTemplateKeys.all,
    queryFn: () => {
      if (!request) throw new Error('No preview request');
      return tmsApi.previewTripCheckpoints(request);
    },
    enabled: !!request,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCreateTripCheckpointTemplate() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateTripCheckpointTemplateRequest>({
    mutationKey: [...tripCheckpointTemplateKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createTripCheckpointTemplate(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: tripCheckpointTemplateKeys.byOrganisation(vars.organisationId) });
    },
  });
}

/** The caller passes `organisationId` beside the body for cache targeting. */
export function useUpdateTripCheckpointTemplate() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateTripCheckpointTemplateRequest }
  >({
    mutationKey: [...tripCheckpointTemplateKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateTripCheckpointTemplate(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({ queryKey: tripCheckpointTemplateKeys.byOrganisation(organisationId) });
    },
  });
}

export function useDeleteTripCheckpointTemplate() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...tripCheckpointTemplateKeys.all, 'delete'],
    mutationFn: ({ id }) => tmsApi.deleteTripCheckpointTemplate(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: tripCheckpointTemplateKeys.byOrganisation(organisationId) });
    },
  });
}
